import { LitElement, html, css } from 'lit';
import { designSystem } from '../styles/design-system.js';
import { authService } from '../services/auth-service.js';

export class LoginPage extends LitElement {
  static properties = {
    _username: { state: true },
    _password: { state: true },
    _rememberMe: { state: true },
    _error: { state: true },
    _errorCode: { state: true },
    _loading: { state: true },
    _showPassword: { state: true }
  };

  static styles = [
    designSystem,
    css`
      :host {
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100vh;
        background: var(--color-bg-tertiary);
        font-family: var(--font-body);
      }

      .login-container {
        width: 100%;
        max-width: 420px;
        padding: var(--space-8);
        animation: fadeUp 0.5s var(--ease-luxury) both;
      }

      @keyframes fadeUp {
        from { opacity: 0; transform: translateY(16px); }
        to { opacity: 1; transform: translateY(0); }
      }

      /* Logo / Brand */
      .brand {
        text-align: center;
        margin-bottom: var(--space-10);
      }

      .brand-logo {
        width: 52px;
        height: 52px;
        background: var(--gradient-brand-hero);
        border-radius: var(--radius-xl);
        display: inline-flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 22px;
        margin-bottom: var(--space-5);
        box-shadow: 0 4px 16px rgba(45, 143, 111, 0.3);
      }

      .brand-title {
        font-family: var(--font-display);
        font-size: var(--text-2xl);
        font-weight: var(--font-black);
        color: var(--color-text-primary);
        letter-spacing: var(--tracking-tight);
        margin-bottom: var(--space-1);
      }

      .brand-subtitle {
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
      }

      /* Card */
      .login-card {
        background: var(--color-bg-elevated);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-xl);
        padding: var(--space-8);
        box-shadow: var(--shadow-lg);
      }

      .card-title {
        font-family: var(--font-display);
        font-size: var(--text-xl);
        font-weight: var(--font-bold);
        color: var(--color-text-primary);
        margin-bottom: var(--space-1);
      }

      .card-desc {
        font-size: var(--text-sm);
        color: var(--color-text-tertiary);
        margin-bottom: var(--space-6);
      }

      /* Form Fields */
      .field {
        margin-bottom: var(--space-5);
      }

      .field-label {
        display: block;
        font-size: var(--text-sm);
        font-weight: var(--font-medium);
        color: var(--color-text-secondary);
        margin-bottom: var(--space-1);
      }

      .input-wrapper {
        position: relative;
      }

      .input-wrapper i {
        position: absolute;
        left: var(--space-3);
        top: 50%;
        transform: translateY(-50%);
        color: var(--color-text-tertiary);
        font-size: 14px;
        pointer-events: none;
        transition: color var(--transition-fast);
      }

      .input-wrapper input {
        width: 100%;
        padding: 10px var(--space-4) 10px 38px;
        background: var(--color-bg-secondary);
        border: 1px solid var(--color-border-primary);
        border-radius: var(--radius-md);
        color: var(--color-text-primary);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        transition: all var(--transition-base);
        line-height: var(--leading-normal);
      }

      .input-wrapper input:focus {
        outline: none;
        border-color: var(--color-primary);
        box-shadow: var(--shadow-glow-primary);
      }

      .input-wrapper input:focus + i,
      .input-wrapper input:focus ~ i {
        color: var(--color-primary);
      }

      .input-wrapper input::placeholder {
        color: var(--color-text-tertiary);
      }

      /* Password toggle */
      .password-toggle {
        position: absolute;
        right: var(--space-3);
        top: 50%;
        transform: translateY(-50%);
        background: none;
        border: none;
        color: var(--color-text-tertiary);
        cursor: pointer;
        font-size: 14px;
        padding: 4px;
        pointer-events: auto;
        transition: color var(--transition-fast);
      }

      .password-toggle:hover {
        color: var(--color-text-secondary);
      }

      /* Remember me / Forgot */
      .form-options {
        display: flex;
        align-items: center;
        justify-content: space-between;
        margin-bottom: var(--space-6);
      }

      .remember-me {
        display: flex;
        align-items: center;
        gap: var(--space-2);
        cursor: pointer;
        font-size: var(--text-sm);
        color: var(--color-text-secondary);
      }

      .remember-me input[type="checkbox"] {
        width: 16px;
        height: 16px;
        accent-color: var(--color-primary);
        cursor: pointer;
      }

      /* Error */
      .error-message {
        background: var(--color-error-light);
        border: 1px solid var(--color-error-bg);
        color: var(--color-error-text);
        padding: var(--space-3) var(--space-4);
        border-radius: var(--radius-md);
        font-size: var(--text-sm);
        margin-bottom: var(--space-5);
        display: flex;
        align-items: center;
        gap: var(--space-2);
        animation: fadeIn 0.2s ease both;
      }

      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }

      .error-message.permission-error {
        background: #FFF8E1;
        border-color: #FFB300;
        color: #E65100;
      }

      .error-message.session-expired {
        background: #FFF8E1;
        border-color: #FFB300;
        color: #E65100;
        font-weight: 500;
      }

      /* Submit Button */
      .submit-btn {
        width: 100%;
        padding: 11px var(--space-5);
        background: var(--color-primary);
        color: white;
        border: none;
        border-radius: var(--radius-lg);
        font-family: var(--font-body);
        font-size: var(--text-sm);
        font-weight: var(--font-semibold);
        cursor: pointer;
        transition: all var(--transition-base);
        box-shadow: var(--shadow-primary-btn);
        display: flex;
        align-items: center;
        justify-content: center;
        gap: var(--space-2);
        letter-spacing: 0.01em;
      }

      .submit-btn:hover:not(:disabled) {
        background: var(--color-primary-hover);
        box-shadow: 0 4px 16px rgba(45, 143, 111, 0.3), 0 2px 4px rgba(45, 143, 111, 0.15);
      }

      .submit-btn:active:not(:disabled) {
        transform: scale(0.98);
      }

      .submit-btn:disabled {
        opacity: 0.6;
        cursor: not-allowed;
      }

      /* Spinner */
      .spinner {
        width: 16px;
        height: 16px;
        border: 2px solid rgba(255,255,255,0.3);
        border-top-color: white;
        border-radius: 50%;
        animation: spin 0.6s linear infinite;
      }

      @keyframes spin {
        to { transform: rotate(360deg); }
      }

      /* Footer */
      .login-footer {
        text-align: center;
        margin-top: var(--space-6);
        font-size: var(--text-xs);
        color: var(--color-text-tertiary);
      }
    `
  ];

  constructor() {
    super();
    this._username = '';
    this._password = '';
    this._rememberMe = false;
    this._error = '';
    this._errorCode = '';
    this._loading = false;
    this._showPassword = false;
  }

  connectedCallback() {
    super.connectedCallback();
    // Check if we arrived here due to session expiration
    const msg = authService.getSessionExpiredMessage();
    if (msg) {
      this._error = msg;
      this._errorCode = 'SESSION_EXPIRED';
    }
  }

  async _handleSubmit(e) {
    e.preventDefault();
    if (this._loading) return;

    this._error = '';
    this._errorCode = '';
    this._loading = true;

    const result = await authService.login(this._username, this._password, this._rememberMe);

    if (result.success) {
      this.dispatchEvent(new CustomEvent('login-success', {
        detail: { user: result.user },
        bubbles: true,
        composed: true
      }));
    } else {
      this._error = result.error;
      this._errorCode = result.errorCode || '';
      this._loading = false;
    }
  }

  _handleKeydown(e) {
    if (e.key === 'Enter') {
      this._handleSubmit(e);
    }
  }

  render() {
    return html`
      <div class="login-container">
        <!-- Brand -->
        <div class="brand">
          <div class="brand-logo">
            <i class="fa-sharp fa-solid fa-waveform-lines"></i>
          </div>
          <div class="brand-title">Voice AI</div>
          <div class="brand-subtitle">Intelligent Reservation Assistant</div>
        </div>

        <!-- Login Card -->
        <div class="login-card">
          <div class="card-title">Sign in</div>
          <div class="card-desc">Enter your credentials to access the dashboard</div>

          ${this._error ? html`
            <div class="error-message ${this._errorCode === 'NO_VOICE_ACCESS' ? 'permission-error' : this._errorCode === 'SESSION_EXPIRED' ? 'session-expired' : ''}">
              <i class="fa-sharp fa-solid ${this._errorCode === 'NO_VOICE_ACCESS' ? 'fa-lock-keyhole' : this._errorCode === 'SESSION_EXPIRED' ? 'fa-clock-rotate-left' : 'fa-circle-exclamation'}"></i>
              ${this._error}
            </div>
          ` : ''}

          <form @submit="${this._handleSubmit}">
            <!-- Username or Email -->
            <div class="field">
              <label class="field-label" for="username">Username or Email Address</label>
              <div class="input-wrapper">
                <input
                  id="username"
                  type="text"
                  placeholder="Username or email"
                  .value="${this._username}"
                  @input="${e => this._username = e.target.value}"
                  ?disabled="${this._loading}"
                  autocomplete="username"
                  required
                />
                <i class="fa-sharp fa-light fa-user"></i>
              </div>
            </div>

            <!-- Password -->
            <div class="field">
              <label class="field-label" for="password">Password</label>
              <div class="input-wrapper">
                <input
                  id="password"
                  type="${this._showPassword ? 'text' : 'password'}"
                  placeholder="Enter your password"
                  .value="${this._password}"
                  @input="${e => this._password = e.target.value}"
                  @keydown="${this._handleKeydown}"
                  ?disabled="${this._loading}"
                  autocomplete="current-password"
                  required
                />
                <i class="fa-sharp fa-light fa-lock-keyhole"></i>
                <button
                  type="button"
                  class="password-toggle"
                  @click="${() => this._showPassword = !this._showPassword}"
                  tabindex="-1"
                >
                  <i class="fa-sharp fa-light ${this._showPassword ? 'fa-eye-slash' : 'fa-eye'}"></i>
                </button>
              </div>
            </div>

            <!-- Options -->
            <div class="form-options">
              <label class="remember-me">
                <input
                  type="checkbox"
                  .checked="${this._rememberMe}"
                  @change="${e => this._rememberMe = e.target.checked}"
                  ?disabled="${this._loading}"
                />
                Remember me
              </label>
            </div>

            <!-- Submit -->
            <button type="submit" class="submit-btn" ?disabled="${this._loading}">
              ${this._loading ? html`<div class="spinner"></div> Signing in...` : 'Sign in'}
            </button>
          </form>
        </div>

        <div class="login-footer">Voice AI System</div>
      </div>
    `;
  }
}

customElements.define('login-page', LoginPage);
